import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import BookingPDF from './BookingPDF';
import logo from '../assets/OceanLogo.png';

interface RoomType {
  type: string;
  count: number;
}

interface BookingFormData {
  name: string;
  phone: string;
  email: string;
  adults: number;
  children: number;
  checkIn: string;
  checkOut: string;
  rooms: RoomType[];
  totalAmount: number;
  advanceAmount: number;
  remarks: string;
}

const BookingForm: React.FC = () => {
  const { register, handleSubmit, control, formState: { errors }, watch } = useForm<BookingFormData>({
    defaultValues: {
      rooms: [{ type: 'Mud Cottage', count: 1 }],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rooms',
    rules: {
      required: "At least one room is required",
    }
  });

  const [formData, setFormData] = useState<BookingFormData | null>(null);
  const [showPdf, setShowPdf] = useState(false);

  const checkInDate = watch('checkIn');
  const today = new Date().toISOString().split('T')[0];

  const validateCheckOutDate = (value: string) => {
    if (!checkInDate || !value) return true;
    return value >= checkInDate || 'Check-out date must be after or equal to check-in date';
  };

  const handleAddRoom = () => {
    append({ type: 'Mud Cottage', count: 1 });
  };

  const onSubmit = (data: BookingFormData) => {
    setFormData(data);
    setShowPdf(true);
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <img 
            src={logo} 
            alt="Ocean Breeze Retreat Logo" 
            style={{ 
              width: '120px',
              height: '120px',
              objectFit: 'contain'
            }} 
          />
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                color: '#1976d2',
                mb: 1
              }}
            >
              Ocean Breeze Retreat
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{
                fontWeight: 500
              }}
            >
              Booking Form
            </Typography>
          </Box>
        </Box>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guest Name"
                {...register('name', { required: 'Name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                {...register('phone', { 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9+\-\s()]{10,}$/,
                    message: 'Please enter a valid phone number'
                  }
                })}
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register('email', { 
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Adults"
                {...register('adults', { 
                  required: 'Number of adults is required',
                  min: { value: 1, message: 'At least 1 adult required' }
                })}
                error={!!errors.adults}
                helperText={errors.adults?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Children"
                {...register('children', { 
                  required: 'Number of children is required',
                  min: { value: 0, message: 'Cannot be negative' }
                })}
                error={!!errors.children}
                helperText={errors.children?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Check-in Date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: today
                }}
                {...register('checkIn', { 
                  required: 'Check-in date is required',
                })}
                error={!!errors.checkIn}
                helperText={errors.checkIn?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Check-out Date"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: checkInDate || today
                }}
                {...register('checkOut', { 
                  required: 'Check-out date is required',
                  validate: validateCheckOutDate
                })}
                error={!!errors.checkOut}
                helperText={errors.checkOut?.message}
              />
            </Grid>
            
            {fields.map((field, index) => (
              <Grid item xs={12} key={field.id}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth error={!!errors.rooms?.[index]?.type}>
                    <InputLabel>Room Type</InputLabel>
                    <Select
                      label="Room Type"
                      defaultValue="Mud Cottage"
                      {...register(`rooms.${index}.type` as const, { 
                        required: 'Room type is required',
                        validate: (value) => value !== '' || 'Room type is required'
                      })}
                    >
                      <MenuItem value="Mud Cottage">Mud Cottage</MenuItem>
                      <MenuItem value="Deluxe Cottage">Deluxe Cottage</MenuItem>
                      <MenuItem value="Super Deluxe Cottage">Super Deluxe Cottage</MenuItem>
                      <MenuItem value="King Room">King Room</MenuItem>
                      <MenuItem value="Suite Room">Suite Room</MenuItem>
                      <MenuItem value="Tent">Tent</MenuItem>
                    </Select>
                    <FormHelperText error={!!errors.rooms?.[index]?.type}>
                      {errors.rooms?.[index]?.type ? 'Room type is required' : ''}
                    </FormHelperText>
                  </FormControl>
                  
                  <TextField
                    type="number"
                    label="Number of Rooms"
                    {...register(`rooms.${index}.count` as const, { 
                      required: 'Number of rooms is required',
                      min: { value: 1, message: 'At least 1 room required' }
                    })}
                    error={!!errors.rooms?.[index]?.count}
                    helperText={errors.rooms?.[index]?.count?.message}
                  />
                  
                  {index > 0 && (
                    <IconButton onClick={() => remove(index)} color="error">
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
            
            <Grid item xs={12}>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddRoom}
                variant="outlined"
              >
                Add Room Type
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Total Amount"
                {...register('totalAmount', { required: 'Total amount is required' })}
                error={!!errors.totalAmount}
                helperText={errors.totalAmount?.message}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Advance Amount"
                {...register('advanceAmount', { required: 'Advance amount is required' })}
                error={!!errors.advanceAmount}
                helperText={errors.advanceAmount?.message}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Remarks"
                {...register('remarks')}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
              >
                Generate Booking Confirmation
              </Button>
            </Grid>
          </Grid>
        </form>

        {showPdf && formData && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <PDFDownloadLink
              document={<BookingPDF data={formData} />}
              fileName="booking-confirmation.pdf"
            >
              {({ loading }) => (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  disabled={loading}
                >
                  {loading ? 'Generating PDF...' : 'Download PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default BookingForm; 